import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import ParentFeedback from './ParentFeedback';
import { useToast } from '../../hook/useToast';
import {
    fetchParentFeedbackStatus,
    requestParentFeedbackForAssessment,
} from '../../services/api';

const NAVY = '#292D5B';
const ORANGE = '#F6A03A';
const GREEN = '#2E7D32';

const Shell = ({ children }) => (
    <Box
        sx={{
            width: '100%',
            maxWidth: 640,
            mx: 'auto',
            borderRadius: '20px',
            overflow: 'hidden',
            backgroundColor: '#fff',
            border: '1px solid #E7EAF1',
            boxShadow: '0 18px 44px rgba(41, 45, 91, 0.08)',
        }}
    >
        {children}
    </Box>
);

const HeaderBanner = ({ icon, tint, eyebrow, title, subtitle, note }) => (
    <Box
        sx={{
            background: 'linear-gradient(135deg, #EAF6F7 0%, #F4FAFB 100%)',
            borderBottom: '1px solid #E1EEF0',
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2.5, sm: 3 },
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
        }}
    >
        <Box
            sx={{
                flexShrink: 0,
                width: 46,
                height: 46,
                borderRadius: '14px',
                backgroundColor: tint,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: 24 },
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography
                sx={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '2px',
                    color: ORANGE,
                    textTransform: 'uppercase',
                    mb: 0.5,
                }}
            >
                {eyebrow}
            </Typography>
            <Typography sx={{ fontSize: { xs: 17, sm: 19 }, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#5B6178', mt: 0.75, lineHeight: 1.6 }}>
                {subtitle}
            </Typography>
            {note && (
                <Typography sx={{ fontSize: 12, color: '#A16207', mt: 1, fontWeight: 600 }}>
                    {note}
                </Typography>
            )}
        </Box>
    </Box>
);

const primaryBtnSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    borderRadius: '12px',
    px: 3,
    py: 1.05,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: '0 10px 22px rgba(246, 160, 58, 0.30)',
    '&:hover': { backgroundColor: '#E8912B', boxShadow: '0 12px 26px rgba(246, 160, 58, 0.4)' },
};

const ghostBtnSx = {
    borderColor: '#D7DBE6',
    color: NAVY,
    borderRadius: '12px',
    px: 2.5,
    py: 1.05,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': { borderColor: '#BFC5D4', backgroundColor: '#F7F8FB' },
};

const navyBtnSx = {
    backgroundColor: NAVY,
    color: '#fff',
    borderRadius: '12px',
    px: 3,
    py: 1.05,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': { backgroundColor: '#1F2348' },
    '&.Mui-disabled': { backgroundColor: '#E1E3EC', color: '#9AA0B2' },
};

const ParentFeedbackGate = ({ onComplete }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [feedbackStatus, setFeedbackStatus] = useState(null);
    const [sendingRequest, setSendingRequest] = useState(false);
    const [showSelfForm, setShowSelfForm] = useState(false);

    const loadStatus = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchParentFeedbackStatus();
            setFeedbackStatus(response?.data || null);
        } catch {
            setFeedbackStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const handleSendToParent = async () => {
        if (sendingRequest) return;

        try {
            setSendingRequest(true);
            const response = await requestParentFeedbackForAssessment();
            setFeedbackStatus(response?.data || null);
            showToast('Feedback request sent to linked parent.', 'success');
        } catch (error) {
            showToast(
                error.response?.data?.error || 'Unable to send feedback request.',
                'error',
            );
        } finally {
            setSendingRequest(false);
        }
    };

    if (showSelfForm) {
        return (
            <ParentFeedback
                onComplete={onComplete}
                introText="Please complete this feedback honestly so your career report can be generated."
                submitButtonLabel="Submit Feedback"
                onBack={() => setShowSelfForm(false)}
            />
        );
    }

    if (loading) {
        return (
            <Shell>
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress sx={{ color: ORANGE }} />
                </Box>
            </Shell>
        );
    }

    const feedbackTaken = Boolean(feedbackStatus?.parent_feedback_taken);
    const requestPending = Boolean(feedbackStatus?.request_pending);
    const linkedParentCount = Number(feedbackStatus?.linked_parent_count || 0);
    const canSendToParent = Boolean(feedbackStatus?.can_send_to_parent);

    if (feedbackTaken) {
        return (
            <Shell>
                <HeaderBanner
                    icon={<CheckRoundedIcon />}
                    tint={GREEN}
                    eyebrow="Final step"
                    title="Parent / Teacher feedback received"
                    subtitle="Every part of your assessment is done â€” your career report can be prepared now."
                />
                <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
                    <Button variant="contained" onClick={onComplete} endIcon={<EastRoundedIcon />} sx={primaryBtnSx}>
                        Continue
                    </Button>
                </Box>
            </Shell>
        );
    }

    if (requestPending) {
        return (
            <Shell>
                <HeaderBanner
                    icon={<HourglassBottomRoundedIcon />}
                    tint={ORANGE}
                    eyebrow="Final step"
                    title="Feedback request sent"
                    subtitle="Your parent can complete this from the parent portal. Refresh here once they're done, or fill it in yourself."
                />
                <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadStatus} sx={ghostBtnSx}>
                        Check again
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<EditRoundedIcon />}
                        onClick={() => setShowSelfForm(true)}
                        sx={primaryBtnSx}
                    >
                        Fill it myself
                    </Button>
                </Box>
            </Shell>
        );
    }

    return (
        <Shell>
            <HeaderBanner
                icon={<HourglassBottomRoundedIcon />}
                tint={ORANGE}
                eyebrow="Final step"
                title="Complete Parent / Teacher feedback"
                subtitle="Choose how this feedback should be completed for your assessment."
                note={
                    !linkedParentCount
                        ? 'Link a parent from your profile before sending a request.'
                        : undefined
                }
            />
            <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadStatus} sx={ghostBtnSx}>
                    Check again
                </Button>
                <Button
                    variant="contained"
                    startIcon={<EditRoundedIcon />}
                    onClick={() => setShowSelfForm(true)}
                    sx={primaryBtnSx}
                >
                    Fill it myself
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SendRoundedIcon />}
                    onClick={handleSendToParent}
                    disabled={!canSendToParent || sendingRequest}
                    sx={navyBtnSx}
                >
                    {sendingRequest ? 'Sendingâ€¦' : 'Send to parent'}
                </Button>
            </Box>
        </Shell>
    );
};

export default ParentFeedbackGate;


